import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { ClipDetailCollection, ClipDetails, MediaItem } from "@gooner/shared";
import { clipKey } from "@gooner/shared";
import { userApi } from "../../lib/api";
import { cx } from "../ui/primitives";
import { formatWhen } from "../lib/format";
import { CrownMark } from "../ui/Crown";
import { GoldName } from "../ui/Gold";
import { MASK } from "../../lib/replayMask";

/* SOURCE_LABELS lived here and named RedGifs and Reddit on screen. It is gone with the
   origin fallback above — a lookup table of platform names is exactly the thing this change
   exists to stop shipping, and leaving it unused would invite the next person to wire it
   back up. */

/** Aggregate stats for a clip: faps, plays, collections, origin, when added, size.
 *  `linkable` renders collection pills as links to /browse (off in the admin panel,
 *  which is intentionally isolated from the rest of the app). */
export function ClipStats({
  clip,
  linkable = true,
  showTechnical = true,
}: {
  clip: MediaItem;
  linkable?: boolean;
  /** Show the technical origin (Source) + Size rows. Off for the user-facing preview. */
  showTechnical?: boolean;
}) {
  const [d, setD] = useState<ClipDetails | null>(null);
  const key = clipKey(clip);

  useEffect(() => {
    let cancelled = false;
    setD(null);
    userApi
      .getClipDetails(key)
      .then((r) => !cancelled && setD(r.details))
      .catch(() => !cancelled && setD(null));
    return () => {
      cancelled = true;
    };
  }, [key]);

  if (!d) return <div className="text-xs text-zinc-600">Loading stats…</div>;

  const w = clip.width ?? d.width;
  const h = clip.height ?? d.height;
  const dur = d.durationSec ?? clip.durationSec;
  /*
   * The UPLOADER, or nothing.
   *
   * This used to fall back to a source label — "RedGifs", "Reddit" — which named the
   * platform a clip was taken from, on screen, to anybody who opened the panel. The wire no
   * longer carries that (see server/src/clip-ids.ts), and printing it here would have made
   * that pointless: there is no sense in making the url opaque while the answer sits in the
   * details table underneath it.
   *
   * An uploader is different and stays: it is a credit for a user who posted to THIS site,
   * not a pointer to somewhere else's catalogue.
   */
  const origin = d.uploader ? `@${d.uploader}` : "—";

  return (
    <dl className="space-y-2 text-xs">
      <Row label="Views">
        <span className="text-zinc-200">👁 {d.views.toLocaleString()}</span>
      </Row>
      {/* Admin only, and beside Views on purpose — it is only meaningful as a ratio.
          Views are reported by the browser; fetches are counted where the bytes leave, so
          the pair is the check on a number the client could otherwise invent. Neither
          bounds the other (immutable caching hides replays, warming inflates fetches), so
          what is being eyeballed here is orders of magnitude, not a difference. */}
      {showTechnical ? (
        <Row label="Fetches">
          <span className="text-zinc-400" title="Cold media fetches from the server — the cross-check on client-reported views. Not a bound in either direction.">
            ⬇ {d.fetches.toLocaleString()}
          </span>
        </Row>
      ) : null}
      <Row label="Faps">
        <span className="font-semibold text-[#f5c518]">
          <CrownMark size={13} /> {d.crowns.toLocaleString()}
        </span>
      </Row>
      <Row label="Edges">
        <span className="text-sky-300">💦 {d.edges.toLocaleString()}</span>
      </Row>
      <Row label="Collections">
        {d.collections.length === 0 ? (
          <span className="text-zinc-600">—</span>
        ) : (
          <div className="flex flex-wrap justify-end gap-1">
            {d.collections.map((c) => (
              <CollectionPill key={c.id} c={c} linkable={linkable} />
            ))}
          </div>
        )}
      </Row>
      {d.uploader ? (
        <Row label="Uploaded by">
          <GoldName name={origin} gold={d.uploaderGold} />
        </Row>
      ) : showTechnical ? (
        <Row label="Source">{origin}</Row>
      ) : null}
      <Row label="Added">{d.createdAt ? formatWhen(d.createdAt) : "—"}</Row>
      {showTechnical && w && h ? (
        <Row label="Size">
          {w}×{h}
          {dur ? ` · ${Math.round(dur)}s` : ""}
        </Row>
      ) : null}
    </dl>
  );
}

/** One collection chip. Editor's Choice, your own, and community collections each
 *  get a distinct color; public ones link to their /browse page, your private ones
 *  to the collection builder. */
function CollectionPill({
  c,
  linkable,
}: {
  c: ClipDetailCollection;
  linkable: boolean;
}) {
  const tone = c.editorsChoice
    ? "bg-[var(--color-accent)]/20 text-[var(--color-accent-soft)]"
    : c.owned
      ? "bg-violet-500/20 text-violet-300"
      : "bg-[var(--color-panel-2)] text-zinc-300";
  const base = cx("rounded-full px-2 py-0.5 text-[11px]", tone);
  // A gold crown marks an official goonlords (Editor's Choice) collection.
  // Editor's Choice names are ours; a member's collection name is masked in replay.
  const label = c.editorsChoice ? (
    <>
      <CrownMark size={11} /> {c.name}
    </>
  ) : (
    <span {...MASK}>{c.name}</span>
  );
  if (!linkable) return <span className={base}>{label}</span>;
  return (
    <Link
      to={c.isPublic ? `/browse/${c.id}` : "/collections"}
      className={cx(base, "hover:brightness-125")}
    >
      {label}
    </Link>
  );
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-zinc-500">{label}</dt>
      <dd className="min-w-0 text-right text-zinc-200">{children}</dd>
    </div>
  );
}
